import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import Header from "@/components/Header";
import { toast } from "sonner";
import { Car, Users, Calendar, MapPin } from "lucide-react";

const LandingPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    organization: "",
    address: "",
    city: "",
    zipCode: "",
    latitude: 37.7749,
    longitude: -122.4194,
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      toast.success("Login successful!");
      if (response.data.user.role === "admin") {
        navigate("/admin");
      }
      else if(response.data.user.role === "master") {
        navigate("/master/dashboard");
      }
      else if(response.data.user.role === "attendant") {
        navigate("/attendant");
      }
      else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(true);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      toast.success("Registration successful!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden px-6 pb-24 pt-10" style={{ background: "linear-gradient(135deg, #1A3C34 0%, #2D5F52 100%)" }}>
        <div className="max-w-7xl mx-auto">
          <Header
            onLoginClick={() => setIsLogin(true)}
            onGetStartedClick={() => setIsLogin(false)}
          />

          <div className="grid lg:grid-cols-2 gap-12 items-center pt-16">
            <div className="text-white fade-in">
              <h1 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight">
                Smart Parking
                <br />
                <span className="text-[#FF6B35]">Management</span>
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                Book parking slots in advance, manage your vehicles, and connect with colleagues for carpooling. Make your daily commute effortless.
              </p>
              <div className="flex gap-4">
                <Button
                  data-testid="get-started-btn"
                  onClick={() => setIsLogin(false)}
                  className="bg-[#FF6B35] hover:bg-[#E55A28] text-white rounded-full px-8 py-6 text-lg font-medium transition-transform hover:scale-105 active:scale-95"
                >
                  Get Started
                </Button>
                <Button
                  data-testid="login-btn"
                  onClick={() => setIsLogin(true)}
                  className="bg-[#F5F2EB] hover:bg-[#EBE5D9] text-[#1A3C34] rounded-full px-8 py-6 text-lg font-medium"
                >
                  Login
                </Button>
              </div>
            </div>

            {/* Auth Form */}
            <Card className="p-8 bg-white/95 backdrop-blur-xl border-none shadow-2xl fade-in">
              <div className="mb-6">
                <div className="flex gap-2 bg-gray-100 rounded-full p-1">
                  <button
                    data-testid="tab-login"
                    onClick={() => setIsLogin(true)}
                    className={`flex-1 py-3 rounded-full font-medium transition-all ${
                      isLogin ? "bg-[#1A3C34] text-white" : "text-gray-600"
                    }`}
                  >
                    Login
                  </button>
                  <button
                    data-testid="tab-register"
                    onClick={() => setIsLogin(false)}
                    className={`flex-1 py-3 rounded-full font-medium transition-all ${
                      !isLogin ? "bg-[#1A3C34] text-white" : "text-gray-600"
                    }`}
                  >
                    Register
                  </button>
                </div>
              </div>

              {isLogin ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium">
                      Email
                    </Label>
                    <Input
                      id="email"
                      data-testid="login-email"
                      type="email"
                      placeholder="your@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] focus:ring-0 rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-gray-700 font-medium">
                      Password
                    </Label>
                    <Input
                      id="password"
                      data-testid="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] focus:ring-0 rounded-lg h-12"
                      required
                    />
                  </div>
                  <Button
                    data-testid="login-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full py-6 text-lg font-medium transition-transform hover:scale-105 active:scale-95"
                  >
                    {loading ? "Loading..." : "Login"}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-gray-700 font-medium">
                        Name
                      </Label>
                      <Input
                        id="name"
                        data-testid="register-name"
                        placeholder="John Doe"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] focus:ring-0 rounded-lg h-12"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-gray-700 font-medium">
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        data-testid="register-phone"
                        placeholder="555-1234"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                        className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] focus:ring-0 rounded-lg h-12"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="reg-email" className="text-gray-700 font-medium">
                      Email
                    </Label>
                    <Input
                      id="reg-email"
                      data-testid="register-email"
                      type="email"
                      placeholder="your@email.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] focus:ring-0 rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-password" className="text-gray-700 font-medium">
                      Password
                    </Label>
                    <Input
                      id="reg-password"
                      data-testid="register-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] focus:ring-0 rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="organization" className="text-gray-700 font-medium">
                      Organization
                    </Label>
                    <Input
                      id="organization"
                      data-testid="register-organization"
                      placeholder="Acme Corp"
                      value={registerData.organization}
                      onChange={(e) => setRegisterData({ ...registerData, organization: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] focus:ring-0 rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="address" className="text-gray-700 font-medium">
                      Address (Demo: Enter any address)
                    </Label>
                    <Input
                      id="address"
                      data-testid="register-address"
                      placeholder="123 Main St"
                      value={registerData.address}
                      onChange={(e) => setRegisterData({ ...registerData, address: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] focus:ring-0 rounded-lg h-12"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city" className="text-gray-700 font-medium">
                        City
                      </Label>
                      <Input
                        id="city"
                        data-testid="register-city"
                        placeholder="San Francisco"
                        value={registerData.city}
                        onChange={(e) => setRegisterData({ ...registerData, city: e.target.value })}
                        className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] focus:ring-0 rounded-lg h-12"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="zipCode" className="text-gray-700 font-medium">
                        ZIP Code
                      </Label>
                      <Input
                        id="zipCode"
                        data-testid="register-zipcode"
                        placeholder="94102"
                        value={registerData.zipCode}
                        onChange={(e) => setRegisterData({ ...registerData, zipCode: e.target.value })}
                        className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] focus:ring-0 rounded-lg h-12"
                        required
                      />
                    </div>
                  </div>
                  <Button
                    data-testid="register-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full py-6 text-lg font-medium transition-transform hover:scale-105 active:scale-95"
                  >
                    {loading ? "Creating Account..." : "Register"}
                  </Button>
                </form>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 px-6 bg-[#F5F2EB]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1A3C34] mb-16 text-center">
            Why Choose Us?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300 fade-in">
              <div className="w-14 h-14 bg-[#1A3C34] rounded-2xl flex items-center justify-center mb-6">
                <Calendar className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-[#1A3C34] mb-3">Book in Advance</h3>
              <p className="text-gray-600 leading-relaxed">
                Reserve your parking spot ahead of time and never worry about availability.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300 fade-in">
              <div className="w-14 h-14 bg-[#FF6B35] rounded-2xl flex items-center justify-center mb-6">
                <Car className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-[#1A3C34] mb-3">Manage Vehicles</h3>
              <p className="text-gray-600 leading-relaxed">
                Add multiple vehicles and manage them easily from your dashboard.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300 fade-in">
              <div className="w-14 h-14 bg-[#1A3C34] rounded-2xl flex items-center justify-center mb-6">
                <Users className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-[#1A3C34] mb-3">Carpool Network</h3>
              <p className="text-gray-600 leading-relaxed">
                Connect with nearby colleagues when parking is full and save the environment.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300 fade-in">
              <div className="w-14 h-14 bg-[#FF6B35] rounded-2xl flex items-center justify-center mb-6">
                <MapPin className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-[#1A3C34] mb-3">Real-time Updates</h3>
              <p className="text-gray-600 leading-relaxed">
                Get instant notifications about your bookings and carpool requests.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;